<script lang="ts">
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { ButtonV2, Container, InputV2, Modal } from '@Shelly/shelly-design-system';
	import { setIRDeviceName } from 'src/helpers/ir_controller_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import IRDeviceCodes from './IRDeviceCodes.svelte';

	export let device: Device;
	export let irDeviceId: number;
	export let irDeviceName = '';
	export let showModal = false;

	let name = '';
	let waitForResponse = false;

	$: if (showModal) name = irDeviceName;

	const handleClose = () => {
		showModal = false;
		name = '';
	};

	const handleSave = async () => {
		const trimmedName = name.trim();
		if (!trimmedName || waitForResponse) return;
		if (trimmedName === irDeviceName) {
			handleClose();
			return;
		}
		if (!get(getDeviceStatus(device.id))?.online) {
			onSettingsResponse({ isok: false }, device);
			return;
		}
		waitForResponse = true;
		const response: SettingRequest = await setIRDeviceName(device, irDeviceId, trimmedName);
		onSettingsResponse(response, device);
		waitForResponse = false;
		if (response?.isok) handleClose();
	};
</script>

<Modal
	{showModal}
	title={$format('ir_controller_ir_device', { default: 'IR Device' })}
	on:close={handleClose}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<InputV2
			id="ir-controller-edit-device-name"
			class="mb-m"
			size="m"
			label={$format('label_name', { default: 'Name' })}
			aria-label={$format('label_name', { default: 'Name' })}
			icon={null}
			maxlength="255"
			bind:value={name}
		/>
		<IRDeviceCodes {device} {irDeviceId} />
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="m"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			on:click={handleClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="m"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			loading={waitForResponse}
			disabled={waitForResponse || !name.trim()}
			aria-label={$format('button_save', { default: 'Save' })}
			on:click={handleSave}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>
